import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {GeneratePresignedUploadUrlGQL} from "../../../generated/graphql";

export interface PresignedUploadUrl {
  url: string;
  fileName: string;
  mimeType: string;
  type: string;
}

export interface FileReference {
  name: string
  type: string
  mimeType: string
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  constructor(
    private readonly generatePresignedUploadUrlGQL: GeneratePresignedUploadUrlGQL,
    private http: HttpClient
  ) {}

  public async uploadFile({ file, type }: {
    file: File,
    type: string
  }): Promise<FileReference | undefined> {
    if (!file) {
      throw new Error('No file provided for upload');
    }

    // Generate a presigned upload URL
    const presignedUploadUrl = await this.generatePresignedUploadUrl(type, file.type);

    try {
      await this.http.put(presignedUploadUrl.url, file, {
        headers: { 'Content-Type': presignedUploadUrl.mimeType }
      }).toPromise();
    } catch (err) {
      console.error('File upload failed', err);
      throw new Error('Failed to upload file to url');
    }

    return {
      name: presignedUploadUrl.fileName,
      type: presignedUploadUrl.type,
      mimeType: presignedUploadUrl.mimeType
    }
  }

  private async generatePresignedUploadUrl(type: string, mimeType: string): Promise<PresignedUploadUrl> {
    const result = await this.generatePresignedUploadUrlGQL.mutate({
      type: type,
      mimeType: mimeType
    }, {
      fetchPolicy: 'no-cache'
    }).toPromise()

    if (!result || !result.data || result.errors || !result.data.generatePresignedUploadUrl) {
      throw new Error(`could not generate presigned upload url: ${result?.errors || 'unknown error'}`);
    }

    return {
      url: result.data.generatePresignedUploadUrl.url,
      type: result.data.generatePresignedUploadUrl.type,
      fileName: result.data.generatePresignedUploadUrl.fileName,
      mimeType: result.data.generatePresignedUploadUrl.mimeType
    };
  }
}
